/* Shared account navigation for library pages; each host keeps its original links. */
(() => {
  'use strict';
  if (window.DACAccountNavigation) return;
  const el = (tag, className, value) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = String(value);
    return node;
  };
  const button = (label, className, action) => {
    const node = el('button', className, label);
    node.type = 'button';
    if (action) node.addEventListener('click', action);
    return node;
  };
  const icon = (path) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.5');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    const line = document.createElementNS(svg.namespaceURI, 'path');
    line.setAttribute('d', path);
    svg.append(line);
    return svg;
  };
  const mounts = new WeakMap();
  function preserveLinks(root) {
    const currentUrl = new URL(location.href);
    const previewTheme = window.Shopify?.theme;
    if (!currentUrl.searchParams.has('preview_theme_id') && ['unpublished', 'development'].includes(String(previewTheme?.role).toLowerCase()) && /^\d+$/.test(String(previewTheme?.id))) {
      currentUrl.searchParams.set('preview_theme_id', String(previewTheme.id));
    }
    root.querySelectorAll('[data-mv-preserve-preview]').forEach(anchor => {
      const raw = anchor.getAttribute('href');
      if (!raw) return;
      const url = new URL(raw, currentUrl);
      if (url.origin !== currentUrl.origin) return;
      ['preview_theme_id', '_ab', '_fd', '_sc'].forEach(key => {
        if (currentUrl.searchParams.has(key)) url.searchParams.set(key, currentUrl.searchParams.get(key));
      });
      anchor.href = url.href;
    });
  }
  function mount(account, root = account) {
    if (!account) return () => {};
    if (mounts.has(account)) return mounts.get(account);
    account.dataset.dacAccountMounted = 'true';
    preserveLinks(root);
    const lifecycle = new AbortController();
    const listen = (target, name, handler, options = {}) => target?.addEventListener(name, handler, { ...options, signal:lifecycle.signal });
    const summary = account.querySelector('summary'), nav = account.querySelector('nav');
    if (!summary || !nav) { delete account.dataset.dacAccountMounted; return () => {}; }
    let sheet = null, cleanupSheet = null, restoreOnClose = true, anchorBottom = 0;
    let pointerInteraction = false;
    const pointerInput = () => { pointerInteraction = true; summary.dataset.dacAccountFocus = 'pointer'; if (sheet) sheet.dataset.dacAccountFocus = 'pointer'; };
    const keyboardInput = event => {
      if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;
      pointerInteraction = false; delete summary.dataset.dacAccountFocus; if (sheet) delete sheet.dataset.dacAccountFocus;
    };
    // detail=0 also covers assistive-technology activation without a physical key.
    const activationInput = event => {
      if (event.detail > 0) pointerInput();
      else { pointerInteraction = false; delete summary.dataset.dacAccountFocus; if (sheet) delete sheet.dataset.dacAccountFocus; }
    };
    const restoreFocus = () => {
      if (!summary.isConnected) return;
      if (pointerInteraction) summary.dataset.dacAccountFocus = 'pointer';
      else delete summary.dataset.dacAccountFocus;
      summary.focus({ preventScroll:true });
    };
    listen(account, 'pointerdown', pointerInput, { capture:true });
    listen(account, 'keydown', keyboardInput, { capture:true });
    listen(account, 'click', activationInput, { capture:true });
    // Clear the pointer exception as soon as focus leaves, so later programmatic
    // or assistive focus receives the normal visible indicator.
    listen(summary, 'blur', () => { delete summary.dataset.dacAccountFocus; });
    listen(document, 'pointerdown', () => {
      if (account.open || sheet || document.activeElement === summary) pointerInput();
    }, { capture:true });
    const compact = () => window.innerWidth <= 767 || window.innerHeight < 560;
    const closeSheet = (restore = true) => {
      if (!sheet) return;
      restoreOnClose = restore;
      const closing = sheet;
      closing.close();
      if (cleanupSheet) cleanupSheet();
    };
    const place = () => {
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      if (sheet) {
        const top = Math.max(12, Math.min(anchorBottom + 6, height - 230));
        sheet.style.setProperty('--mv-menu-top', top + 'px');
        sheet.style.setProperty('--mv-menu-height', Math.max(96, Math.min(510, height - top - 12)) + 'px');
        sheet.style.setProperty('--mv-menu-bottom', Math.max(12, window.innerHeight - (viewport?.offsetTop || 0) - height + 12) + 'px');
        return;
      }
      if (!account.open) return;
      const bounds = summary.getBoundingClientRect(), top = viewport?.offsetTop || 0;
      const below = Math.max(0, top + height - bounds.bottom - 12), above = Math.max(0, bounds.top - top - 12);
      const useAbove = below < 220 && above > below;
      account.dataset.mvPlacement = useAbove ? 'above' : 'below';
      nav.style.maxHeight = Math.max(80, useAbove ? above : below) + 'px';
    };
    const mode = () => {
      summary.setAttribute('aria-haspopup', compact() ? 'dialog' : 'true');
      if (sheet && !compact()) closeSheet();
      place();
    };
    const openSheet = () => {
      if (sheet) return;
      anchorBottom = summary.getBoundingClientRect().bottom;
      const node = el('dialog', 'dac-mv-account-dialog');
      if (typeof node.showModal !== 'function') return false;
      const header = el('div', 'dac-mv-dialog-head'), title = el('h2', '', account.dataset.mvAccountTitle);
      title.id = root.id + '-account-title';
      node.id = root.id + '-account-panel';
      node.setAttribute('aria-labelledby', title.id);
      const close = button('', 'dac-mv-close', () => closeSheet());
      close.setAttribute('aria-label', account.dataset.mvCloseLabel);
      close.append(icon('m6 6 12 12M18 6 6 18'));
      if (window.DACCloseControl) window.DACCloseControl.apply(close, account.dataset.mvCloseLabel);
      header.append(title, close);
      const signout = nav.querySelector('[data-mv-account-signout]'), footer = el('div', 'dac-mv-account-footer');
      const savedOverflow = [document.documentElement, document.body].map(element => ({ element, value:element.style.getPropertyValue('overflow'), priority:element.style.getPropertyPriority('overflow') }));
      const savedMaxHeight = nav.style.maxHeight;
      if (signout) footer.append(signout);
      nav.classList.add('dac-mv-account-panel-links'); nav.style.maxHeight = '';
      node.append(header, nav, footer); document.body.append(node);
      account.open = false; sheet = node; restoreOnClose = true;
      if (pointerInteraction) node.dataset.dacAccountFocus = 'pointer';
      summary.setAttribute('aria-expanded', 'true'); summary.setAttribute('aria-controls', node.id);
      for (const { element } of savedOverflow) element.style.setProperty('overflow', 'hidden');
      let cleaned = false;
      cleanupSheet = () => {
        if (cleaned) return; cleaned = true;
        if (signout) nav.append(signout);
        account.append(nav); nav.classList.remove('dac-mv-account-panel-links'); nav.style.maxHeight = savedMaxHeight;
        for (const { element, value, priority } of savedOverflow) {
          if (value) element.style.setProperty('overflow', value, priority); else element.style.removeProperty('overflow');
        }
        sheet = null; cleanupSheet = null; node.remove(); account.open = false;
        summary.setAttribute('aria-expanded', 'false'); summary.removeAttribute('aria-controls');
        if (restoreOnClose) restoreFocus();
      };
      node.addEventListener('pointerdown', pointerInput, { capture:true });
      node.addEventListener('keydown', keyboardInput, { capture:true });
      node.addEventListener('click', activationInput, { capture:true });
      node.addEventListener('close', cleanupSheet, { once:true });
      node.addEventListener('cancel', event => { event.preventDefault(); keyboardInput({ key:'Escape' }); closeSheet(); });
      node.addEventListener('click', event => {
        if (event.target.closest('a')) {
          const anchor = event.target.closest('a');
          const target = new URL(anchor.href, location.href);
          const sameDocument = target.origin === location.origin && target.pathname === location.pathname && target.search === location.search;
          if (sameDocument || anchor.target === '_blank' || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) closeSheet();
          return;
        }
        if (event.target !== node) return;
        const bounds = node.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeSheet();
      });
      place(); node.showModal(); nav.scrollTop = 0; close.focus({ preventScroll:true });
      return true;
    };
    listen(summary, 'click', event => {
      if (!compact()) return;
      if (openSheet()) event.preventDefault();
    });
    listen(account, 'toggle', () => {
      summary.setAttribute('aria-expanded', String(Boolean(sheet) || account.open));
      if (!sheet) place();
    });
    listen(account, 'keydown', event => {
      if (event.key === 'Escape' && account.open) {
        event.preventDefault(); event.stopPropagation(); account.open = false; restoreFocus();
      }
    });
    listen(document, 'click', event => { if (!account.contains(event.target)) account.open = false; });
    listen(window, 'resize', mode);
    listen(window.visualViewport, 'resize', place);
    listen(window.visualViewport, 'scroll', place);
    listen(window, 'scroll', place, { passive:true });
    listen(window, 'pagehide', () => closeSheet(false));
    const dispose = () => {
      closeSheet(false);
      lifecycle.abort();
      account.open = false;
      delete account.dataset.dacAccountMounted; delete summary.dataset.dacAccountFocus;
      mounts.delete(account);
    };
    mounts.set(account, dispose);
    mode();
    return dispose;
  }

  const start = () => {
    document.querySelectorAll('[data-dac-account-navigation]').forEach(account => mount(account));
    document.querySelectorAll('.dac-account-breadcrumbs, .dac-account-hero').forEach(preserveLinks);
    const trail = document.querySelector('[data-dac-wishlist-breadcrumbs]');
    if (!trail || trail.dataset.mounted) return;
    trail.dataset.mounted = 'true';
    const parent = trail.querySelector('[data-dac-wishlist-parent]');
    const item = trail.querySelector('[data-dac-wishlist-current]');
    const sync = () => {
      if (!trail.isConnected) return;
      const view = document.querySelector('.wishlist-products-view');
      const visible = view && getComputedStyle(view).display !== 'none';
      const name = visible ? view.querySelector('.wishlist-select__toggle-title')?.textContent.trim() : '';
      const label = item.querySelector('span');
      if (label.textContent !== (name || '')) label.textContent = name || '';
      item.hidden = !name;
      if (name) parent.removeAttribute('aria-current'); else parent.setAttribute('aria-current', 'page');
    };
    const listRoot = trail.closest('.dac-wishlist-page');
    if (listRoot) new MutationObserver(sync).observe(listRoot, { childList:true, subtree:true });
    window.addEventListener('popstate', sync);
    sync();
  };
  window.DACAccountNavigation = { mount, preserveLinks };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once:true }); else start();
  document.addEventListener('shopify:section:load', start);
})();

